import React from "react"
import {useParams} from "react-router-dom"
import {useAccessToken, useIsUnitReadonlyUser} from "../../../services/auth"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import {MainSection} from "../../../containers/MainSection/MainSection"
import TitleBar from "../../../components/TitleBar/TitleBar"
import {
	DataTable,
	DataTableBody,
	DataTableCard,
	DataTableHead,
	TablePending,
} from "../../../components/DataTable/DataTable"
import {
	getAppConfigByOrgId,
	getWhiteLabelAppUsersByOrgId,
	AppConfig,
	WhiteLabelAppUser,
} from "../../../resources/whiteLabel"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {getOrg, getOrgName} from "../../../resources/org"
import {JWTProviders} from "../../../resources/orgSettings"
import {KeyValueCardView} from "../../../components/RenderKeyValueCard/KeyValueCardView"

import Icon from "../../../components/Icon/Icon"
import {useModal} from "react-modal-hook"
import ManagedSolutionsConfigModal from "./ManagedSolutionsConfigModal"
import {ManagedSolutionsUserRow} from "./ManagedSolutionsUserRow"

function AppConfigSection({
	config,
	onRefresh,
	orgId,
	readOnly,
}: {
	config: AppConfig
	onRefresh: () => void
	orgId: string
	readOnly: boolean
}) {
	const configDetails = {
		"JWT Issuer": config.attributes.jwtIssuer || "Not configured",
		"JWT Provider": (() => {
			const provider = config.attributes.jwtProvider?.provider
			if (!provider) return "Not configured"
			// Check if provider is a valid enum value and not Custom
			if (Object.values(JWTProviders).includes(provider as JWTProviders) && provider !== JWTProviders.Custom) {
				return provider
			}
			return "Custom"
		})(),
		"JWKS Path": (() => {
			const provider = config.attributes.jwtProvider?.provider
			if (provider === JWTProviders.Custom) {
				return "Not applicable for custom provider"
			}
			return config.attributes.jwtProvider?.jwksPath || "Not configured"
		})(),
		"JWT Audience": (() => {
			const provider = config.attributes.jwtProvider?.provider
			if (provider === JWTProviders.CustomWithJWKS) {
				return config.attributes.jwtProvider?.audience || "Not configured"
			}
			return "Not applicable for this provider"
		})(),
		"JWT Public Key": config.attributes.jwtPublicKey ? "Configured" : "Not configured",
		"Application Form Version": config.attributes.applicationFormVersion,
		"End User Config URL": config.attributes.endUserConfigUrl || "Not configured",
		"Find End Users URL": config.attributes.findEndUsersUrl || "Not configured",
		"Banking Page URL": config.attributes.bankingPageURL || "Not configured",
		"Wire Enabled": config.attributes.isWireEnabled ? "Enabled" : "Disabled",
		"Quickbooks Enabled": config.attributes.isQuickbooksEnabled ? "Enabled" : "Disabled",
		"App Users Enabled": config.attributes.isAppUsersEnabled ? "Enabled" : "Disabled",
		"Account Creation Enabled": config.attributes.isAccountCreationEnabled !== false ? "Enabled" : "Disabled",
	}

	const [showEditConfigModal, hideEditConfigModal] = useModal(() => (
		<ManagedSolutionsConfigModal
			mode="edit"
			orgId={orgId}
			existingConfig={config}
			close={hideEditConfigModal}
			onSuccess={() => {
				onRefresh()
				hideEditConfigModal()
			}}
		/>
	))

	const editButton = (
		<button className="button is-small is-info" onClick={showEditConfigModal}>
			Edit
		</button>
	)

	return (
		<KeyValueCardView
			title="App Configuration"
			details={configDetails}
			singleColumn={true}
			additionalHeaderContent={readOnly ? undefined : editButton}
		/>
	)
}

function ManagedSolutionsUsersSection({
	users,
	onRefresh,
	readOnly,
}: {
	users: WhiteLabelAppUser[]
	onRefresh: () => void
	readOnly: boolean
}) {
	return (
		<div className="managed-solutions-users-section">
			<DataTableCard className="managed-solutions-users-card">
				<TitleBar title={`Managed Solutions Users (${users.length})`} />
				<DataTable
					fullHeight={true}
					isEmpty={users.length === 0}
					noContentText={"No Managed Solutions users found"}
					style={{overflowY: "scroll"}}
				>
					<DataTableHead>
						<tr>
							<th>External User ID</th>
							<th>User Email</th>
							<th>User Role</th>
							<th>Customer ID</th>
							<th>Status</th>
							<th>Application Form ID</th>
							<th>Created At</th>
							{!readOnly && <th>Actions</th>}
						</tr>
					</DataTableHead>
					<DataTableBody>
						{users.map((user) => {
							const customerId = user.relationships.customer?.data.id
							return (
								<ManagedSolutionsUserRow
									key={user.id}
									user={user}
									customerId={customerId}
									onRefresh={onRefresh}
									readOnly={readOnly}
								/>
							)
						})}
					</DataTableBody>
				</DataTable>
			</DataTableCard>
		</div>
	)
}

function NoConfigSection({orgId, onRefresh, readOnly}: {orgId: string; onRefresh: () => void; readOnly: boolean}) {
	const [showCreateConfigModal, hideCreateConfigModal] = useModal(() => (
		<ManagedSolutionsConfigModal
			mode="create"
			orgId={orgId}
			close={hideCreateConfigModal}
			onSuccess={() => {
				onRefresh()
				hideCreateConfigModal()
			}}
		/>
	))

	return (
		<div className="card">
			<div className="card-content has-text-centered">
				<div className="content">
					<h3>No Configuration Found</h3>
					<p>{"This org doesn't have an app configuration yet."}</p>
					{!readOnly && (
						<button className="button is-success" onClick={showCreateConfigModal}>
							<Icon icon="interface-add-1" size={12} />
							Create Configuration
						</button>
					)}
				</div>
			</div>
		</div>
	)
}

export default function ManagedSolutionsAdminOrg() {
	const {orgId} = useParams<{orgId: string}>()
	const accessToken = useAccessToken()
	const isUnitReadonly = useIsUnitReadonlyUser()

	const configResult = useAsyncResult(
		() => (orgId ? getAppConfigByOrgId(accessToken, orgId) : Promise.reject(new Error("No orgId"))),
		[orgId, accessToken]
	)
	const usersResult = useAsyncResult(
		() => (orgId ? getWhiteLabelAppUsersByOrgId(accessToken, orgId) : Promise.reject(new Error("No orgId"))),
		[orgId, accessToken]
	)
	const orgResult = useAsyncResult(
		() => (orgId ? getOrg(accessToken, orgId) : Promise.reject(new Error("No orgId"))),
		[orgId, accessToken]
	)

	const refreshData = () => {
		// Force refresh by re-calling the hooks with new dependency values
		window.location.reload()
	}

	const breadcrumbs = [
		{text: "Admin Dashboard", path: "/admin"},
		{text: "Managed Solutions", path: "/admin#tab=Managed+Solutions"},
	]

	return (
		<>
			<AsyncResultComponent
				asyncResult={orgResult}
				pendingComponent={<TitleBar title={`Org ${orgId}`} breadcrumbs={breadcrumbs} />}
			>
				{({value: org}) => <TitleBar title={getOrgName(org)} breadcrumbs={breadcrumbs} />}
			</AsyncResultComponent>

			<MainSection>
				<div className="managed-solutions-org-page">
					<div className="columns">
						<div className="column is-3">
							<AsyncResultComponent asyncResult={configResult} pendingComponent={<TablePending />}>
								{({value: config}) =>
									orgId ? (
										config ? (
											<AppConfigSection
												config={config}
												onRefresh={refreshData}
												orgId={orgId}
												readOnly={isUnitReadonly}
											/>
										) : (
											<NoConfigSection orgId={orgId} onRefresh={refreshData} readOnly={isUnitReadonly} />
										)
									) : (
										<div className="notification is-warning">Org ID is required</div>
									)
								}
							</AsyncResultComponent>
						</div>

						<div className="column is-9">
							<AsyncResultComponent asyncResult={usersResult} pendingComponent={<TablePending />}>
								{({value: users}) => (
									<ManagedSolutionsUsersSection users={users} onRefresh={refreshData} readOnly={isUnitReadonly} />
								)}
							</AsyncResultComponent>
						</div>
					</div>
				</div>
			</MainSection>
		</>
	)
}
